import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { TableLink } from '@repo/ui/data-table-cells'
import { PeopleTwoIcon, PlusIcon, ResetIcon } from '@repo/ui/icons'
import { useMutation } from '@tanstack/react-query'

import { API } from '../../api/apis.config'
import { resetPcrPassword } from '../../api/pcr/reset-pcr-password'
import DynamicDataTable from '../../components/elements/dynamic-table'
import UserNavBar from '../../components/elements/nav-bar'
import { Paths } from '../../lib/constants/path'
import { Pcr } from '../../lib/types/models/pcr/pcr'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../utils/functions/format-phone-number'

const PcrsList = () => {
  const { navigateWithEvent, navigate } = useNavigator()

  const { mutateAsync } = useMutation({
    mutationFn: resetPcrPassword,
    onError: showAxiosError
  })
  const columns: Column<Pcr>[] = [
    {
      accessorKey: 'id',
      header: 'PCR ID',
      enableSorting: true
    },
    {
      accessorKey: 'firstName',
      header: 'Prénom',
      enableSorting: true,
      cell: ({ row }) => StringFormatter.capitalize(row.original.firstName)
    },
    {
      accessorKey: 'lastName',
      header: 'NOM',
      enableSorting: true,
      cell: ({ row }) => row.original.lastName.toUpperCase()
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Numéro de téléphone',
      cell: ({ row }) => formatPhoneNumber(row.original.phoneNumber)
    },
    {
      accessorKey: 'user.email',
      header: 'Email',
      enableSorting: true
    },
    {
      accessorKey: 'applicationsToBeContactedSum',
      header: 'Applications à contacter',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          text={row.original.applicationsToBeContactedSum?.toString()}
          variant={row.original.applicationsToBeContactedSum ? 'warning' : 'grey'}
        />
      )
    },
    {
      accessorKey: 'id',
      header: 'Tableau de bord',
      cell: ({ row }) => (
        <TableLink
          to={Paths.PCR_KANBAN_BOARD.replace(':id', row.original.id.toString())}
          label="Voir le tableau de bord"
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="PCR" icon={PeopleTwoIcon} />
      <div className="h-4" />
      <DynamicDataTable
        title="Liste de tous les PCR disponibles"
        columns={columns}
        selectable
        endpoint={API.pcr.getAll.url}
        deleteMessage={{
          name: 'pcr',
          attributeName: 'id',
          deleteOne: (pcr) =>
            `Êtes-vous sûr de vouloir supprimer le pcr "${StringFormatter.capitalize(pcr.firstName)} ${pcr.lastName.toUpperCase()}" ?`,
          deleteMany: (pcrs) =>
            `Êtes-vous sûr de vouloir supprimer ces pcrs "${pcrs
              .map(
                (pcr) =>
                  `${StringFormatter.capitalize(pcr.firstName)} ${pcr.lastName.toUpperCase()}`
              )
              .join('", "')}" ?`
        }}
        onRowClick={(row, event) => {
          navigateWithEvent(Paths.PCR_UPDATE_PAGE.replace(':id', row.id.toString()), event)
        }}
        allowSearchParam
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Ajouter un nouveau PCR',
            fn: () => {
              navigate(Paths.PCR_CREATE_PAGE)
            }
          },
          {
            type: 'multiple',
            icon: ResetIcon,
            label: 'Réinitialiser le mot de passe',
            successMessage: 'Le mot de passe a été réinitialisé avec succès',
            fn: async (pcrs) => {
              const ids = pcrs.map((pcr) => pcr.id)
              await mutateAsync({ ids })
            }
          }
        ]}
      />
    </div>
  )
}

export default PcrsList
